// current below is useful for logging data from the store
// import { current } from "@reduxjs/toolkit";
import { clamp, min, max, values, round, indexOf } from "lodash";
import { side, snapDownToIncrement } from "utilities/mathematic";
import { MIC_CATEGORIES } from "types/models/microphone";

// HELPER
// Create standard positioning configuration
const createPositioningConfig = (deviceType, displayName, extraNotes) => ({
  title: (name, isEmpty) => `${name} ${isEmpty ? 'does not have equipment' : `${displayName} positioning detail`}`,
  widths: [4, 12, 12, 12, 12],
  list: 'positioningDetail',
  deviceType,
  adjust: true,
  notes: extraNotes 
    ? ['Horizontal and Vertical distances are referenced from top-left corner or diagram', extraNotes]
    : ['Horizontal and Vertical distances are referenced from top-left corner or diagram']
});

/**
 * Max pickup distance (m) per acoustic tier, Poor→Perfect, for the pole-mountable ceiling mics.
 *
 * ONE array shared by `tcm_x` and `dcm_x`: the Bar + Expansion Ceiling Mic Placement spec treats
 * the Tree Frog Ethernet Ceiling Mic (DCM-X) as equivalent to the Parlé TCM-X for ALL calculation
 * purposes (see Documentation/specs/expansion-ceiling-mic-pole-mount-amendment.md), so their
 * default mount heights are identical BY CONSTRUCTION rather than by two literals that happen to
 * match. If product ever gives DCM-X its own pickup distances, split this into two consts — every
 * consumer below is already parameterized by the array, so nothing silently keeps using TCM-X's
 * numbers.
 */
const CEILING_MIC_MIC_DATA = [1.8, 2.3, 3.0, 3.75, 5.0];

// PERFORMANCE OPTIMIZATION: Pre-calculate expensive ceiling mic calculations
// This cache prevents the expensive side() math operations from running repeatedly
const ceilingMicCalculationCache = {};

/**
 * Tallest mount height (above the talker) that still keeps the coverage radius >= 1 m, i.e. the
 * inverse of the coverage-radius formula: `side(maxDist, 1) + talkerHeight`.
 *
 * @param {number} acousticIndex - index into `micData` / `constants.acoustics`.
 * @param {number} talkerHeight
 * @param {number[]} [micData] - the MODEL'S OWN pickup distances. Defaults to the shared ceiling
 *   mic array so existing 2-arg callers (forms.js's "ceiling too high" UI condition) are
 *   unaffected. Previously this array was hardcoded in the body, which meant a caller passing a
 *   model with different micData would have silently received TCM-X's cap.
 * @returns {number} The acoustics-optimal maximum mount height, in metres.
 */
const getCachedCeilingMicCalculation = (acousticIndex, talkerHeight, micData = CEILING_MIC_MIC_DATA) => {
  const maxDist = micData?.[acousticIndex];
  // Key on the distance actually used, not the index — two models with different micData must
  // not collide on the same cache entry.
  const cacheKey = `${maxDist}_${talkerHeight}`;

  if (!ceilingMicCalculationCache[cacheKey]) {
    // Perform the expensive calculation once and cache it
    const max = round(side(maxDist, 1) + talkerHeight, 4);
    ceilingMicCalculationCache[cacheKey] = max;
  }

  return ceilingMicCalculationCache[cacheKey];
};

/**
 * Height range configuration for CBC 2500 bars (both CM and PM).
 * Fixed range: 6' (1.8288m) to 12' (3.6576m)
 */
const cbcHeightRange = [1.8288, 3.6576];

/**
 * Centralized CBC (Parlé CBC 2500) height calculation function.
 * Calculates the default height for CBC bar microphones based on ceiling height.
 *
 * @param {Object} area - The area object containing ceilingHeight.
 * @returns {number} The calculated height in meters.
 *
 * Formula: ceiling - 0.5', clamped to 6' - 12' range
 * - Must maintain 0.5 feet (0.1524m) clearance from ceiling
 * - Min height is 6 feet (1.8288m)
 * - Max height is 12 feet (3.6576m)
 */
const calculateCBCHeight = (area) => {
  const [minHeight, maxHeight] = cbcHeightRange;

  // Calculate default height: ceiling - 0.5' clearance
  const defaultHeight = area.ceilingHeight.min - 0.1524;

  // Clamp to the valid range (6' to 12')
  return Math.max(minHeight, Math.min(maxHeight, defaultHeight));
};

/**
 * Canonical default mount height for a pole-mountable ceiling microphone (`tcm_x`, `dcm_x`).
 *
 * ONE implementation shared by both models — they are specified as equivalent for all
 * calculation purposes, so a single parameterized function is what actually guarantees they
 * agree. `height()` and `isAcousticsLimited()` are both derived from this one call rather than
 * duplicating the acoustics-cap math a second time just to answer a yes/no question about it.
 *
 * Formula: mount FLUSH with the ceiling, capped by the acoustics-optimal coverage-radius height
 * for the room's current acoustics tier, floored at the 6' minimum mic height, and — only when
 * the cap actually pulls the mic off the ceiling — floored to a 0.5' pole increment.
 *
 * ⚠️ Do NOT add a `ceiling - 0.5'` mounting-clearance term here. Ceiling mics mount flush by
 * default; a pole — and the TCM-X-PM / PMA-2000-PE hardware `calculateTCMXAccessories` sizes off
 * the resulting mounting distance — is introduced only when the acoustics cap below, or a manual
 * height edit, genuinely brings the mic down. A 0.1524 m default clearance would sit 2.4 mm above
 * that helper's own 0.15 m `MIN_MOUNTING_HEIGHT` threshold, silently billing one base pole kit
 * per mic on every default design.
 *
 * `isAcousticsLimited` answers "is the ACOUSTICS cap what's constraining this height?": true
 * whenever the room's acoustics tier pulls the mount below the ceiling (a Poor-tier room,
 * typically). Used to fire an informational toast on the actions that establish this default
 * (see room.ts's `setExpansionMicType` / `placeExpansionMicAtLocation` / the ceiling-height sync
 * in `setExpansionMicrophones`, and logic.js's `resetArea` 'acoustics' case) — since the height
 * is correct from the moment it's set, there's no later validation-driven "adjusting height"
 * correction (validation.js) to piggyback that information on for this specific case.
 *
 * @param {Object} area
 * @param {number[]} micData - the model's own max pickup distances per acoustic tier.
 * @returns {{ height: number, isAcousticsLimited: boolean }}
 */
const computeCeilingMicHeightInfo = (area, micData) => {
  const ceilingHeight = area.ceilingHeight.min;
  const minimumMicHeight = constants.mounting?.minimumMicHeight || 1.8288; // 6 feet minimum
  // Flush mount: the ceiling itself is the upper bound.
  const maxAllowedHeight = ceilingHeight;

  const acousticsIndex = constants.acoustics.indexOf(area.acoustics);
  // An index of -1 already yields undefined here, so this one guard covers both the
  // unknown-acoustics and missing-micData cases.
  const micDataValue = micData?.[acousticsIndex];

  if (!micDataValue) {
    const height = Math.max(minimumMicHeight, Math.min(2.4384, maxAllowedHeight));
    return { height, isAcousticsLimited: false };
  }

  // Uses the shared side()-based cache (getCachedCeilingMicCalculation) rather than a plain
  // Math.sqrt(micDataValue), which isn't the inverse of the coverage-radius formula and forced
  // mics several feet lower than necessary (e.g. ~9.6ft cap at "Good" instead of ~13.2ft).
  const talkerHeight = constants.area.talkerHeight;
  const maxOptimalHeight = getCachedCeilingMicCalculation(acousticsIndex, talkerHeight, micData);

  const targetHeight = Math.min(ceilingHeight, maxOptimalHeight);
  const constrainedHeight = Math.max(minimumMicHeight, Math.min(targetHeight, maxAllowedHeight));

  // Snap DOWN — a max constraint is never rounded up: rounding to the NEAREST increment could
  // round past the acoustics cap, shrinking the coverage radius below the 1 m that cap exists to
  // guarantee and inflating the default mic count (a 20'x14' no-furniture Poor room went to 12
  // mics instead of 6). Skipped entirely for a flush mount: there is no pole to snap to a section
  // length, and snapping would otherwise drag a ceiling that isn't itself a multiple of 0.5'
  // (say 9'7") down to 9'6", inventing a 1" pole and its hardware.
  const increment = constants.poleSections?.imperial || 0.1524; // 0.5 feet
  const height = constrainedHeight >= ceilingHeight
    ? ceilingHeight
    : snapDownToIncrement(constrainedHeight, increment);

  // The pre-clamp comparison, not the post-clamp one: acoustics is what's limiting this height
  // whenever the raw ceiling height alone would have allowed a HIGHER mount than the
  // acoustics-optimal cap does.
  const isAcousticsLimited = ceilingHeight > maxOptimalHeight;

  return { height, isAcousticsLimited };
};

// BMA360D max pickup distances (m) per acoustic level: Poor→Perfect.
// ⚠️ Working estimates pending real-world validation (scaled from TCM-X × 1.707).
// Shared by both SKU variants and by the auto-lower helper below.
const BMA360D_MIC_DATA = [3.07, 3.93, 5.12, 6.40, 8.54];

// PIG-439 / WDT-73: the BMA360D recessed tile mic has a spec-defined install-height range
// of 7'–20'. This is DELIBERATELY tighter on the low end than the generic 6'
// (constants.mounting.minimumMicHeight) used by pole-mounted ceiling mics — a BMA360D must
// not mount below 7'. The effective upper bound is further capped by the ceiling (the mic
// can never sit above it). Both heightRange (which the setMicrophones reducer clamp reads)
// and the auto-lower target (bma360dMaxAllowableHeight) honour this 7' floor so a user can
// neither type nor auto-lower below it.
const BMA360D_MIN_HEIGHT = 2.1336; // 7'
const BMA360D_MAX_HEIGHT = 6.096;  // 20'

/**
 * PIG-295 AC2/AC3: maximum mount height where the BMA360D coverage radius is still
 * ≥ `minRadius`. Solves the spec radius formula for height:
 *   radius² = maxDist² − (height − talkerHeight)²
 *   ⇒ height = talkerHeight + √(maxDist² − minRadius²)
 * The result is clamped to the existing heightRange bounds (`mounting.minimumMicHeight`
 * on the low end, ceiling on the high end). Returns `null` if the formula is
 * unsatisfiable at the configured acoustic level (e.g. maxDist ≤ minRadius).
 *
 * Used by calculateDevicesMiddleware to auto-lower the mic when the formula at ceiling
 * height would produce an invalid or sub-1 m radius.
 */
const bma360dMaxAllowableHeight = (area, minRadius = 1.0) => {
  const idx = constants.acoustics?.indexOf(area.acoustics);
  if (idx == null || idx < 0) return null;
  const maxDist = BMA360D_MIC_DATA[idx];
  if (maxDist <= minRadius) return null;
  const talkerHeight = constants.area?.talkerHeight ?? 1.2009;
  // PIG-439 / WDT-73: floor the auto-lower target at the BMA360D 7' minimum, not the generic
  // 6' mounting.minimumMicHeight — the AC requires height to stay within 7'–20' even after auto-lower.
  const minMountHeight = BMA360D_MIN_HEIGHT;
  const candidateHeight = talkerHeight + Math.sqrt(maxDist * maxDist - minRadius * minRadius);
  const ceilingHeight = area.ceilingHeight?.min;
  if (typeof ceilingHeight !== 'number') return null;
  return Math.max(minMountHeight, Math.min(ceilingHeight, candidateHeight));
};

// BMA360D ceiling tile mic factory — only modelName and tile size differ between SKUs
const makeBma360d = (modelName, tileSize) => ({
  modelName,
  physicalDimensions: { x: tileSize, y: tileSize }, // mic footprint in meters, for canvas icon scaling
  // Default to the ceiling, but never above the 20' spec install max (rooms with >20' ceilings).
  height: (area) => Math.min(BMA360D_MAX_HEIGHT, area.ceilingHeight.min),
  // PIG-295 AC2/AC3 (absorbing PIG-399's structural change): allow below-ceiling mounts
  // so calculateDevicesMiddleware can auto-lower the mic when coverage radius would be
  // invalid or sub-1 m. The user's manual height adjustments stay within these bounds too.
  // PIG-439 / WDT-73: the valid range is 7'–20' (BMA360D-specific, tighter than the generic
  // 6' floor) — the upper bound is further capped by the ceiling. The setMicrophones reducer
  // clamps against this range, so typing 6' now snaps up to the 7' floor.
  heightRange: (area) => [
    BMA360D_MIN_HEIGHT,
    Math.min(BMA360D_MAX_HEIGHT, area.ceilingHeight.min),
  ],
  coverage: (area) => {
    const ck = values(area.microphones.coverage).some(x => x);
    const hasTable = Boolean(area.table.shape);
    return ck ? area.microphones.coverage : {
      focused: hasTable,
      full_room: !hasTable
    };
  },
  micData: BMA360D_MIC_DATA,
  maxUnits: 3, // Dante network / interface capacity hard cap
  maxAllowableHeight: bma360dMaxAllowableHeight,
});

/**
 * Dual-imager camera wide-angle imager configuration.
 * Shared between Vidi 280 standalone camera and VBC 2800 bar camera.
 * @type {Object}
 */
const CAMERA_WIDE_IMAGER = {
  /**
   * The coverage angle of the wide-angle imager (full field of view).
   * @type {number}
   */
  coverageAngle: 109,
  /**
   * The coverage radius of the wide-angle imager.
   * @type {number}
   */
  coverageRadius: 5,
  /**
   * The limit radius of the wide-angle imager.
   * @type {number}
   */
  limitRadius: 9
};

/**
 * Dual-imager camera zoom imager configuration.
 * Shared between Vidi 280 standalone camera and VBC 2800 bar camera.
 * @type {Object}
 */
const CAMERA_ZOOM_IMAGER = {
  /**
   * The coverage angle of the zoom imager.
   * @type {number}
   */
  coverageAngle: 86,
  /**
   * The coverage radius of the zoom imager.
   * @type {number}
   */
  coverageRadius: 7,
  /**
   * The limit radius of the zoom imager.
   * @type {number}
   */
  limitRadius: 12
};

/**
 * Constants for the Conference Room Designer tool.
 * @constant
 * @type {Object}
 */
const constants = {
  tool: {
    ver: 'MDT-V2.0 / 4.0.2',
    fullName: 'Conference Room Designer',
    /**
     * The two parameters below are used in the current live version to redirect the tool to the current version in Cornerstone
     * If deploying to live, make certain these are uncommented!
     */
    // name: 'conferenceroom',
    // goto: 'https://support.biamp.com/Design_Tools/Conference_Room_Designer',
    path: {
      short: '/crd',
      long: '/conference_room_designer'
    },
    cookie: 'CRDSeenTutorial',
    container: false,
    areaName: 'Room',
    maximumAreas: 8,
    addAreaLabel: 'A ROOM',
    landingMenu: 'floor_plan',

    /**
     * localCalc is a flag to trigger (or not) local device calculations
     * This is required as WD relies on the API response for device arrays
     */
    localCalc: true, // flag to allow local calculation of devices 

    /**
     * Maximum devices specifies what's the limit of devices that can be addded to a design
     * Set here as different devices might have different limits
     */
    maximumDevices: {
      microphones: 64,
      speakers: 64,
      cameras: 1
    },

  },

  /**
   * Area-specific constants.
   * @type {Object}
   */
  area: {
    min: 1.8288, // minimum area dimension 6'
    max: 30.48, // maximum area DImension 100'
    // fixme: talkerHeight is a also used in CD so it should be moved to a global context or imported from here
    talkerHeight: 1.2009,
    table2wall: 0.7,
    /**
     * Speaker aiming calculation constants.
     * @type {Object}
     */
    speakerAiming: {
      talkerHeight: 1.8288,      // ~6 ft default speaker height for wall speakers
      listenerHeight: 1.2192,    // ~4 ft seated listener height
      minVerticalAngle: -90,     // Maximum downward aim in degrees (straight down)
      maxVerticalAngle: 0,       // Maximum upward aim (level) in degrees
      defaultTargetDepth: 0.6    // Target point as % of room depth
    }
  },

  /**
   * Ceiling-specific constants.
   * @type {Object}
   */
  ceiling: {
    min: 2.4384,
    max: 4.572,
    microphoneThreshold: 4.2672  // 14 feet - threshold for switching from ceiling to pendant microphone
  },

  // fixme: ceilingTiles is a also used in CD so it should be moved to a global context or imported from here 
  /**
   * Ceiling tile dimensions in meters.
   * @type {Object.<string, {x: number, y: number}>}
   */
  ceilingTiles: {
    "no_ceiling_tiles": { x: 0, y: 0 },
    "2'_x_2'": { x: 0.6096, y: 0.6096 },
    "2'_x_4'": { x: 0.6096, y: 1.2192 },
    "4'_x_2'": { x: 1.2192, y: 0.6096 },
    "600mm_x_600mm": { x: 0.6, y: 0.6 }
  },

  // Imperial ceiling tile keys — used to determine which BMA360D SKU to pre-select.
  // Shared between calculateDevicesMiddleware and menus.js to avoid duplication.
  bmaImperialTileKeys: new Set(["2'_x_2'", "2'_x_4'", "4'_x_2'"]),

  // Marketing SKU names for the BMA360D variants. These strings surface in the
  // equipment list (stored as area.microphones.modelName). PIG-420: the user-facing
  // "Model" field in the configurator collapses both variants under a single read-only
  // "BMA360D" label (see menus.js); the actual SKU only appears in the equipment list.
  bmaModelNames: {
    bma360d_24: 'BMA360D',     // 24" tile variant
    bma360d_600: 'BMA360D-I',  // 600mm tile variant
  },

  // PIG-402 / PIG-294 AC1: returns the BMA360D MicKey to pre-select based on ceiling
  // tile config and unit system. Defined as a regular function so `this.bmaImperialTileKeys`
  // resolves to the Set above.
  preselectBma360dModel(tileKey, isMeter) {
    if (this.bmaImperialTileKeys.has(tileKey)) return 'bma360d_24';
    if (tileKey === '600mm_x_600mm') return 'bma360d_600';
    return isMeter ? 'bma360d_600' : 'bma360d_24';
  },

  // fixme: acoustics is a also used in CD so it should be moved to a global context or imported from here 
  /**
   * Acoustics quality levels.
   * @type {string[]}
   */
  acoustics: ['poor', 'fair', 'good', 'great', 'perfect'],

  /**
   * Table configurations for different shapes.
   * @type {Object.<string, Object>}
   */
  tables: {
    rectangle: {
      /**
       * Minimum dimensions for a rectangle table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 0.9144, width: 0.9144 },
      /**
       * Calculates default dimensions for a rectangle table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length: min([constants.tableMaximumSize.length, length - 3.048]), width: clamp(min([constants.tableMaximumSize.width, width - 3.048]), 0.9144, 1.2192) }),
      endTable: false,
    },
    racetrack: {
      /**
       * Minimum dimensions for a racetrack table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 0.9144, width: 0.9144 },
      /**
       * Calculates default dimensions for a racetrack table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length:  min([constants.tableMaximumSize.length, length - 3.048]), width: clamp(min([constants.tableMaximumSize.width, width - 3.048]), 0.9144, 1.2192) }),
      endTable: false,
    },
    boat: {
      /**
       * Minimum dimensions for a boat table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 0.9144, width: 0.3048},
      /**
       * Calculates default dimensions for a boat table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length:  min([constants.tableMaximumSize.length, length - 3.048]), width: clamp(min([constants.tableMaximumSize.width, width - 3.048]), 0.9144, (min([constants.tableMaximumSize.length, length - 3.048])) * .13 + 1.143) }),
      endTable: false,
    },
    round: {
      /**
       * Minimum dimensions for a round table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 0.6096, width: 0.6096 },
      /**
       * Calculates default dimensions for a round table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length: clamp(min([constants.tableMaximumSize.length, min([length, width]) - 3.048]), 0.6096, 3.048 ), width: clamp(min([constants.tableMaximumSize.length, min([length, width]) - 3.048]), 0.6096, 3.048 ) }),
      endTable: false,
    },
    bullet: {
      /**
       * Minimum dimensions for a bullet table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 0.9144, width: 0.9144 },
      /**
       * Calculates default dimensions for a bullet table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length: min([constants.tableMaximumSize.length, length - 1.524]), width: clamp(min([constants.tableMaximumSize.width, width - 3.048]), 0.9144, 1.8288) }),
      endTable: true,
      canPlaceAgainstFrontWall: true,
    },
    keystone: {
      /**
       * Minimum dimensions for a keystone table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 1.2192, width: 0.9144 },
      /**
       * Calculates default dimensions for a keystone table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length: min([constants.tableMaximumSize.length, length - 1.524]), width: min([constants.tableMaximumSize.width, max([width - 2.4384, 0.9144])]) }),
      endTable: true,
      canPlaceAgainstFrontWall: true,
    },
    ushape: {
      /**
       * Minimum dimensions for a ushape table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 0.9144, width: 0.9144 },
      /**
       * Calculates default dimensions for a ushape table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length: length - 1.524, width: max([width - 3.048, 0.9144]) }),
      endTable: true,
      canPlaceAgainstFrontWall: true,
    },
    vshape: {
      /**
       * Minimum dimensions for a vshape table.
       * @type {Object}
       * @property {number} length - Minimum length in meters.
       * @property {number} width - Minimum width in meters.
       */
      minimum: { length: 1.2192, width: 0.9144 },
      /**
       * Calculates default dimensions for a vshape table based on room dimensions.
       * @function
       * @param {Object} dimensions - The room dimensions.
       * @param {number} dimensions.length - The room length in meters.
       * @param {number} dimensions.width - The room width in meters.
       * @returns {Object} The default table dimensions.
       * @property {number} length - The calculated default length.
       * @property {number} width - The calculated default width.
       */
      default: ({ length, width }) => ({ length: length - 1.524, width: max([width - 2.4384, 0.9144]) }),
      endTable: true,
      canPlaceAgainstFrontWall: true,
    },
  },

  /**
   * Define the maximum size of room tables
   * @type {Object}
   */
  tableMaximumSize: {
    length: 9.15,
    width: 6.1
  },

  /**
   * @constant
   * @name defaultPlots
   * @type {Object}
   * @description Defines the default plot types for different device categories in the Conference Room Designer.
   * 
   * @property {string} microphones - The default plot type for microphones, set to 'micDistance'.
   * @property {string} speakers - The default plot type for speakers, set to 'relativeAverage'.
   * @property {null} camera - The default plot type for cameras, set to null (indicating no default plot).
   */
  defaultPlots: {
    microphones: 'micDistance',
    speakers: 'relativeAverage',
    cameras: 'cameraCoverage'
  },

  iconMultiplier: 0.00536,
  devices: ['microphones', 'speakers', 'camera', 'equipmentlist'],
  hideDevices: ['floorplan', 'ceiling', 'furnishing', 'acoustics', 'equipmentlist'],

  /**
   * @name customSettings
   * @type {Object}
   * @description Specifies the values to be set in the Redux store when certain properties are customized.
   * These settings determine how the UI and store should respond when users make custom selections.
   * 
   * @property {Object} microphones - Custom settings for microphones
   * @property {Object} microphones['microphones.coverage'] - Coverage options for microphones
   * 
   * @property {Object} speakers - Custom settings for speakers
   * @property {Object} speakers['speakers.coverage'] - Coverage options for speakers
   */
  customSettings: {
    microphones: {
      'coverage': {
        full_room: false,
        focused: false,
      }
    },
    speakers: {
      'coverage': {
        full_room: false,
        focused: false,
      }
    },
    cameras: {
      // 'cameras.': true
    }
  },


  /**
   * Default overlap configuration.
   * Used in calculateMicPlacement helper function to calculate initial microphone quantity
   * @type {Object}
   * @returns {Object} An object with full_room and focused default values.
   */
  defOverlap: { // This values are used to determine the initial miccount
    full_room: 1.6,
    // PIG-619 investigation: getOverlaps() (utilities/geometric.ts) only ever produces overlap
    // values in [MIN_OVERLAP=1.4142, MAX_OVERLAP=2.0]. 1.3 sits below that floor, so
    // `closest(overlaps, 1.3)` always resolves to whichever achievable overlap is nearest
    // 1.4142 — the tightest spacing / MAXIMUM mic count for the current coverage box. That's why
    // a focused-coverage ceiling/pendant mic's default can land on the densest (last) slider
    // position instead of a middle pick (e.g. reported repro: 44'x36'x14' room + 30'x4' table,
    // ceiling mic — jumpPoints [5,6,12], overlaps [1.6442,1.4942,1.4142] — 1.3 picks 12, not 6).
    // A per-mode-only fix (e.g. raising this to 1.5) was tried and reverted: it fixes that case
    // but flips the same "always max" failure onto OTHER rooms/mic types instead (verified: a
    // default-size room + pendant mic + focused coverage regresses from its correct default (2)
    // to 1). Kept at 1.3 deliberately — no single value (or per-mic-type split) covers every
    // room/table/mic-model combination without moving the bug elsewhere. If a genuinely more
    // robust fix (e.g. position-based selection rather than closest-overlap-value) is revisited,
    // start from this note plus the PIG-619 ticket history.
    focused: 1.3
  },

  /**
   * Default overlap configuration for bar + expansion ceiling mic configurations.
   * Specifies how far a bar mic's coverage is expected to extend before expansion
   * ceiling mics are needed. Used in calcExpansionCeilingMicPlacement.
   * @type {Object}
   */
  defBarOverlap: {
    full_room: 1.6,
    focused: 1.3
  },

  /**
   * Maximum number of expansion ceiling microphones allowed in a bar + ceiling mic
   * configuration.
   * @type {number}
   */
  maxExpansionMics: 4,

  /**
   * Display-name placeholders for the Devio UCX expansion mics surfaced when the user
   * accepts the bar + expansion-mic recommendation. The underlying placement and
   * equipment-lookup logic still uses the Tesira `tcm_x` / `ttm_x` model keys because
   * the real DCM-X / DTM-X catalog entries (Devio UCX Ethernet mic families) have
   * not been added yet — that's a separate epic. Until then, these strings let the UI
   * show the correct platform-appropriate model name everywhere the expansion mic is
   * surfaced (selector text input, layout entries, equipment summaries).
   *
   * TODO: replace with real DCM-X / DTM-X equipment entries once the Devio UCX
   * hardware catalog work lands. At that point delete this section and switch the
   * reducer + placement code to assign the real `dcm_x` / `dtm_x` model keys.
   * @type {Object}
   */
  expansionMicPlaceholders: {
    ceiling: 'Parlé DCM-X',
    table:   'Parlé DTM-X',
  },

  /**
   * Overlap factor for bar + table mic configurations.
   * Controls how far each expansion table mic pulls back from the bar coverage boundary
   * so that its coverage circle overlaps with the bar's, ensuring no gap at the transition.
   * overlapDistance = micCoverageRadius × defBarTableOverlap
   *
   * Value 1.6 per the Bar + Expansion Tabletop Mic Placement spec §5. Pull-back sensitivity
   * on long/narrow tables (it can drop the expansion mic count to 1) is tracked as spec open
   * question T.2 — to be validated empirically with the spec author before changing.
   * @type {number}
   */
  defBarTableOverlap: 1.6,

  /**
   * Target spacing factor for table microphones (bar + expansion and standalone alike).
   * Mic count along a path = effectiveLength / (defTableOverlap × micCoverageRadius); a larger
   * value spaces mics farther apart (fewer mics). Spec §5 value 1.34. Extracted here from the
   * literal that was hard-coded in the placement code (spec open question T.6) so it is no
   * longer a magic number.
   * @type {number}
   */
  defTableOverlap: 1.34,

  /**
   * Speaker-spacing factor for bar + expansion ceiling speaker configurations, expressed as a
   * multiple of the speaker coverage radius:
   *   pitch (centre-to-centre spacing) = ceilingSpkrRadius × defBarSpeakerOverlap[mode]
   * At 2.0 the spacing equals the coverage diameter, so adjacent coverage circles just TOUCH
   * (~0 overlap) — the smallest overlap that still gives contiguous coverage along the row, and
   * the fewest speakers. This also drops otherwise-redundant far columns (e.g. the 32×20 / 22×4
   * third column). Lower values pack the circles tighter (1.6 → 0.4r overlap; 1.3 → ~0.7r).
   * Note: circles just touching leave small gaps at the tangent points and between rows; dial
   * back to ~1.9 if the heatmap shows cool spots. The first-column anchor no longer pulls back
   * into the bar zone — it's pinned so its ring just meets the bar's coverage edge regardless of
   * this factor (see `calcBarExpansionPlacement`).
   * @type {Object}
   */
  defBarSpeakerOverlap: {
    full_room: 2.0,
    focused: 2.0
  },

  /**
   * Minimum number of expansion-speaker rows in FOCUSED (table) mode. A narrow table is covered
   * by a single centreline row geometrically, but that row points down the gap between the two
   * seating sides. Forcing at least two rows straddles the table so a row of speakers sits over
   * the listeners on each long side. Wider tables that already need >= 2 rows are unaffected
   * (placement uses max(geometric rows, this)). Full-room mode (no table) ignores this.
   * Set to 1 to restore purely geometric row counts.
   * @type {number}
   */
  expansionSpeakerMinRowsFocused: 2,

  /**
   * Maximum number of expansion ceiling speakers allowed in a bar + expansion speaker
   * configuration. Also caps auto-placement, which is NOT port-capped (setExpansionSpeakerLayout
   * stores the placed layout as-is), so it must not exceed the 12 C-IC6 that 1–2 expansion
   * ceiling mics support (port accounting spec §6.1) — a larger auto-placed count would trip
   * the Devio UCX capacity fallback. The spec's 16 (bar only, two DPA-450BP, §4.4) is a
   * theoretical maximum it expects placement to stay well below. Manual adds are further
   * limited by the port budget (`maxExpansionSpeakerCountFor`).
   * @type {number}
   */
  maxExpansionSpeakers: 12,

  /**
   * SPL decay threshold (dB) used to compute bar speaker reach via the inverse-square law:
   *   barReach = refDistance × 10^(barSpeakerDecayThreshold / 20)
   * Consistent with the −6 dB coverage-angle convention used for ceiling speakers.
   * @type {number}
   */
  barSpeakerDecayThreshold: 6,

  /**
   * Default reference distance (meters) from the bar wall to the nearest listening position
   * when no table is present in the design (~5 ft).
   * @type {number}
   */
  barSpeakerNoTableRefDistance: 1.524,

  /**
   * DEBUG ONLY: draw a dotted coverage circle at each expansion/ceiling speaker (its own −6 dB
   * radius) and at the bar (its SPL-decay reach). Not a user-facing control — flip this flag to
   * visualize speaker coverage while debugging placement, then set back to false for production.
   * @type {boolean}
   */
  debugSpeakerCoverageCircles: false,

  /**
   * DEBUG ONLY: draw the dashed coverage-box outlines (MuiExpansionBox) — the expansion ceiling
   * mic box, expansion table mic box, expansion ceiling/pendant speaker box, and the primary
   * ceiling/pendant speaker's own coverage box. Not a user-facing control — flip this flag to
   * visualize the boxes placement is computed against, then set back to false for production.
   * @type {boolean}
   */
  debugCoverageBoxes: false,

  /**
   * Pole section configurations for ceiling microphone mounting.
   * @type {Object}
   */
  poleSections: {
    metric: 0.15,      // 0.15m increments
    imperial: 0.1524   // 0.5' in meters
  },

  /**
   * Mounting constraints for ceiling microphones.
   * @type {Object}
   */
  mounting: {
    minimumPoleHeight: 0.1524,        // 0.5' minimum pole mounting height
    minimumMicHeight: 1.8288,         // 6' minimum height (standing person)
    maximumMountingDistance: 9.1      // 30' maximum mounting distance
  },

  equipment: {
    /**
     * Distance from the left wall to the center of a wall-mounted bar mic (VBC/ABC).
     * Used by getDeviceLayouts (placement), room.ts (snap-to-wall), and any coverage
     * visualizer that draws from the bar's actual position.
     */
    wallMountedBarOffset: 0.134,

    /**
     * Microphone configurations for different types.
     * @type {Object.<string, Object>}
     */
    microphones: {
      /**
       * Ceiling microphone configuration.
       * @type {Object}
       */
      tcm_x: {
        modelName: 'Parlé TCM-X',
        /**
         * Default mount height: flush with the ceiling, unless the room's acoustics tier
         * requires a lower mount to keep the coverage radius >= 1 m. See
         * `computeCeilingMicHeightInfo` above for the full formula — shared verbatim with
         * `dcm_x`, which is specified as equivalent to this mic for all calculation purposes.
         * Used in the setMicrophones reducer to set the initial microphone height.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The calculated optimal height.
         */
        height: (area) => computeCeilingMicHeightInfo(area, CEILING_MIC_MIC_DATA).height,
        /**
         * True when the room's acoustics tier — not the ceiling itself — is what's constraining
         * this mic's default height. See `computeCeilingMicHeightInfo` above.
         * @function
         * @param {Object} area
         * @returns {boolean}
         */
        isAcousticsLimited: (area) => computeCeilingMicHeightInfo(area, CEILING_MIC_MIC_DATA).isAcousticsLimited,
        /**
         * Determines the valid height range for a ceiling microphone.
         * Used in the setMicrophones reducer to clamp the microphone height.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number[]} An array with min and max height values for adjustment.
         */
        heightRange: (area) => [1.8288, area.ceilingHeight.min],
        /**
         * Determines the coverage settings for a ceiling microphone.
         * Used in the setMicrophones reducer to set the initial coverage or preserve existing coverage.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {Object} An object with focused and full_room boolean properties.
         */
        coverage: (area) => {
          const ck = values(area.microphones.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.microphones.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        /**
         * Microphone data for different acoustic conditions.
         * @type {number[]}
         */
        micData: CEILING_MIC_MIC_DATA,
      },
      /**
       * Ceiling EXPANSION microphone (Devio UCX "DCM-X") configuration. Functionally identical
       * to the primary ceiling mic (`tcm_x`) — same height/coverage/acoustic data — but a distinct
       * model key so the bar + ceiling-expansion mic can be tracked, rendered, and listed as its
       * own device rather than masquerading as a primary TCM-X. Replaces the prior
       * `expansionMicPlaceholders.ceiling` display-name hack.
       * @type {Object}
       */
      dcm_x: {
        modelName: 'Parlé DCM-X',
        // Same shared formula AND the same micData reference as tcm_x — the spec treats DCM-X as
        // equivalent to TCM-X for all calculation purposes, so the two heights are identical by
        // construction. See `computeCeilingMicHeightInfo` above.
        height: (area) => computeCeilingMicHeightInfo(area, CEILING_MIC_MIC_DATA).height,
        /**
         * True when the room's acoustics tier — not the ceiling itself — is what's constraining
         * this mic's default height. See `computeCeilingMicHeightInfo` above.
         * @function
         * @param {Object} area
         * @returns {boolean}
         */
        isAcousticsLimited: (area) => computeCeilingMicHeightInfo(area, CEILING_MIC_MIC_DATA).isAcousticsLimited,
        heightRange: (area) => [1.8288, area.ceilingHeight.min],
        coverage: (area) => {
          const ck = values(area.microphones.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.microphones.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        micData: CEILING_MIC_MIC_DATA,
      },
      /**
       * Video bar microphone configuration.
       * @type {Object}
       */
      vbc_2500a: {
        modelName: 'Parlé VBC 2500a',
        cameraKey: 'barCamera',
        /**
         * Returns a constant height for a video bar microphone.
         * @function
         * @returns {number} The fixed height of the talker.
         */
        height: () => constants.area.talkerHeight,
        /**
         * Returns a fixed height range for a video bar microphone.
         * @function
         * @returns {number[]} An array with two identical values representing the fixed height.
         */
        heightRange: () => Array(2).fill(constants.area.talkerHeight),
        /**
         * Returns a fixed coverage setting for a video bar microphone.
         * @function
         * @returns {Object} An object with focused and full_room set to false.
         */
        coverage: () => ({
          focused: false,
          full_room: false
        }),
        /**
 * Microphone data for different acoustic conditions.
 * @type {number[]}
 */
        micData: [3.5, 4.3, 5.6, 7.0, 9.8],
      },
      /**
       * VBC 2800 video bar microphone configuration.
       * @type {Object}
       */
      vbc_2800: {
        modelName: 'Parlé VBC 2800',
        cameraKey: 'barCamera2800',
        /**
         * Returns a constant height for a video bar microphone.
         * @function
         * @returns {number} The fixed height of the talker.
         */
        height: () => constants.area.talkerHeight,
        /**
         * Returns a fixed height range for a video bar microphone.
         * @function
         * @returns {number[]} An array with two identical values representing the fixed height.
         */
        heightRange: () => Array(2).fill(constants.area.talkerHeight),
        /**
         * Returns a fixed coverage setting for a video bar microphone.
         * @function
         * @returns {Object} An object with focused and full_room set to false.
         */
        coverage: () => ({
          focused: false,
          full_room: false
        }),
        /**
 * Microphone data for different acoustic conditions - same as VBC 2500.
 * @type {number[]}
 */
        micData: [3.5, 4.3, 5.6, 7.0, 9.8],
      },
      /**
       * Audio bar microphone configuration.
       * @type {Object}
       */
      abc_2500a: {
        modelName: 'Parlé ABC 2500a',
        /**
         * Returns a constant height for a audio bar microphone.
         * @function
         * @returns {number} The fixed height of the talker.
         */
        height: () => constants.area.talkerHeight,
        /**
         * Returns a fixed height range for a audio bar microphone.
         * @function
         * @returns {number[]} An array with two identical values representing the fixed height.
         */
        heightRange: () => Array(2).fill(constants.area.talkerHeight),
        /**
         * Returns a fixed coverage setting for a audio bar microphone.
         * @function
         * @returns {Object} An object with focused and full_room set to false.
         */
        coverage: () => ({
          focused: false,
          full_room: false
        }),
        /**
         * Microphone data for different acoustic conditions.
         * @type {number[]}
         */
        micData: [3.5, 4.3, 5.6, 7.0, 9.8],
      },
      /**
       * CBC 2500 CM video bar microphone configuration (Cable Mount with Camera).
       * @type {Object}
       */
      cbc_2500_cm: {
        modelName: 'Parlé CBC 2500 CM',
        /**
         * Returns the default height for CBC 2500 CM - optimized for ceiling mount.
         * Default is ceiling - 0.5', clamped to the 6'-12' range.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The calculated height.
         */
        height: (area) => calculateCBCHeight(area),
        /**
         * Returns the valid height range for CBC 2500 CM.
         * Fixed range: 6' (1.8288m) to 12' (3.6576m)
         * @returns {number[]} An array with min and max height values.
         */
        heightRange: () => cbcHeightRange,
        /**
         * Returns a fixed coverage setting for CBC 2500 CM.
         * @function
         * @returns {Object} An object with focused and full_room set to false.
         */
        coverage: () => ({
          focused: false,
          full_room: false
        }),
        /**
         * Microphone data for different acoustic conditions.
         * CBC bars have smaller coverage radii than VBC/ABC bars due to focused pickup pattern.
         * Values scaled to produce stadium coverage of ~18ft x 10.5ft at "Good" acoustics.
         * @type {number[]}
         */
        micData: [1.0, 1.3, 1.6, 2.1, 2.9],
      },
      /**
       * CBC 2500 PM audio bar microphone configuration (Pole Mount).
       * @type {Object}
       */
      cbc_2500_pm: {
        modelName: 'Parlé CBC 2500 PM',
        /**
         * Returns the default height for CBC 2500 PM - optimized for pole mount.
         * Default is ceiling - 0.5', clamped to the 6'-12' range.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The calculated height.
         */
        height: (area) => calculateCBCHeight(area),
        /**
         * Returns the valid height range for CBC 2500 PM.
         * Fixed range: 6' (1.8288m) to 12' (3.6576m)
         * @returns {number[]} An array with min and max height values.
         */
        heightRange: () => cbcHeightRange,
        /**
         * Returns a fixed coverage setting for CBC 2500 PM.
         * @function
         * @returns {Object} An object with focused and full_room set to false.
         */
        coverage: () => ({
          focused: false,
          full_room: false
        }),
        /**
         * Microphone data for different acoustic conditions.
         * CBC bars have smaller coverage radii than VBC/ABC bars due to focused pickup pattern.
         * Values scaled to produce stadium coverage of ~18ft x 10.5ft at "Good" acoustics.
         * @type {number[]}
         */
        micData: [1.0, 1.3, 1.6, 2.1, 2.9],
      },
      /**
       * Pendant microphone microphone configuration.
       * @type {Object}
       */
      tcm_1: {
        modelName: 'Parlé TCM-1',
        /**
         * Calculates the default height for a pendant microphone.
         * Used in the setMicrophones reducer to set the initial microphone height.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The calculated height, which is the minimum ceiling height.
         */
        height: (area) => Math.min(2.4384, area.ceilingHeight.min), // this needs to change 
        /**
         * Determines the valid height range for a pendant microphone.
         * Used in the setMicrophones reducer to clamp the microphone height.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number[]} An array with two identical values representing the min and max height.
         */
        heightRange: (area) => [1.828, area.microphones.maxHeight],
        /**
         * Determines the coverage settings for a pendant microphone.
         * Used in the setMicrophones reducer to set the initial coverage or preserve existing coverage.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {Object} An object with focused and full_room boolean properties.
         */
        coverage: (area) => {
          const ck = values(area.microphones.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.microphones.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        /**
         * Microphone data for different acoustic conditions.
         * @type {number[]}
         */
        micData: [1.5, 2.0, 2.4, 3.0, 4.0],
      },
      /**
       * Tabletop microphone configuration.
       * @type {Object}
       */
      ttm_x: {
        modelName: 'Parlé TTM-X',
        /**
         * Returns a constant height for a tabletop microphone.
         * @function
         * @returns {number} The fixed height of the talker.
         */
        height: () => constants.area.talkerHeight,
        /**
         * Returns an empty array for a tabletop microphone height range.
         * Used in the setMicrophones reducer to clamp the microphone height.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {Object} Always returns an empty array.
         */
        heightRange: () => [],
        /**
         * Returns a fixed coverage setting for a tabletop microphone.
         * @function
         * @returns {Object} An object with focused set to true and full_room set to false.
         */
        coverage: () => ({
          focused: true,
          full_room: false
        }),
        /**
         * Microphone data for different acoustic conditions.
         * @type {number[]}
         */
        micData: [1.0, 1.3, 1.7, 2.1, 2.5],
      },
      // BMA360D ceiling tile mics (MIC_CATEGORIES.CEILING_TILE). The marketing SKU
      // is surfaced via modelName ("BMA360D" / "BMA360D-I") and appears in the
      // equipment list. The user-facing Model field in the configurator shows a
      // single collapsed "BMA360D" label — variant is resolved automatically.
      bma360d_24: makeBma360d('BMA360D', 0.6096),       // imperial: 24"×24" tile
      bma360d_600: makeBma360d('BMA360D-I', 0.6),       // metric: 600mm×600mm tile
    },

    /**
     * Categorical lookup sets for microphone feature detection.
     * Use these instead of reading boolean flags off config objects.
     *
     * @example
     *   const isBar = constants.equipment.micCategories.BAR.has(area.microphones.model);
     */
    micCategories: MIC_CATEGORIES,

    /**
     * Speaker configurations for different types.
     * @type {Object.<string, Object>}
     */
    speakers: {
      /**
       * Ceiling speaker configuration.
       * @type {Object}
       */
      ceilingspkr: {
        modelName: 'Desono C-IC6',
        isCeilingSpkr: true,
        mountingType: 'ceiling',
        icon: () => 'speaker',
        height: (area) => area.ceilingHeight.min,
        heightRange: (area) => Array(2).fill(area.ceilingHeight.min),
        coverage: (area) => {
          const hasTable = Boolean(area.table.shape);
          return {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 39,
        sensitivity: 86.6,
        polarData: { 0: -0.9, 5: -0.4, 10: -0.9, 15: -1.5, 20: -1.35, 25: -1.3, 30: -1.3, 35: -1.95, 40: -1.85, 45: -2.1, 50: -2.75, 55: -2.25, 60: -3.25, 65: -3.6, 70: -2.85, 75: -2.85, 80: -4.5, 85: -7.2, 90: -9.3 }
      },
      'ceilingspkr-lp': {
        modelName: 'Desono C-IC6LP',
        isCeilingSpkr: true,
        mountingType: 'ceiling',
        icon: () => 'speaker',
        height: (area) => area.ceilingHeight.min,
        heightRange: (area) => Array(2).fill(area.ceilingHeight.min),
        coverage: (area) => {
          const hasTable = Boolean(area.table.shape);
          return {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 39,
        sensitivity: 86.6,
        polarData: { 0: -0.9, 5: -0.4, 10: -0.9, 15: -1.5, 20: -1.35, 25: -1.3, 30: -1.3, 35: -1.95, 40: -1.85, 45: -2.1, 50: -2.75, 55: -2.25, 60: -3.25, 65: -3.6, 70: -2.85, 75: -2.85, 80: -4.5, 85: -7.2, 90: -9.3 }
      },
      'ceilingspkr-lp-taa': {
        modelName: 'Desono C-IC6LP-TAA',
        isCeilingSpkr: true,
        mountingType: 'ceiling',
        icon: () => 'speaker',
        height: (area) => area.ceilingHeight.min,
        heightRange: (area) => Array(2).fill(area.ceilingHeight.min),
        coverage: (area) => {
          const hasTable = Boolean(area.table.shape);
          return {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 39,
        sensitivity: 86.6,
        polarData: { 0: -0.9, 5: -0.4, 10: -0.9, 15: -1.5, 20: -1.35, 25: -1.3, 30: -1.3, 35: -1.95, 40: -1.85, 45: -2.1, 50: -2.75, 55: -2.25, 60: -3.25, 65: -3.6, 70: -2.85, 75: -2.85, 80: -4.5, 85: -7.2, 90: -9.3 }
      },
      'ceilingspkr-mask6c': {
        modelName: 'Desono MASK6C',
        isCeilingSpkr: true,
        mountingType: 'ceiling',
        icon: () => 'speaker',
        height: (area) => area.ceilingHeight.min,
        heightRange: (area) => Array(2).fill(area.ceilingHeight.min),
        coverage: (area) => {
          const hasTable = Boolean(area.table.shape);
          return {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 39,
        sensitivity: 86.6,
        polarData: { 0: -0.9, 5: -0.4, 10: -0.9, 15: -1.5, 20: -1.35, 25: -1.3, 30: -1.3, 35: -1.95, 40: -1.85, 45: -2.1, 50: -2.75, 55: -2.25, 60: -3.25, 65: -3.6, 70: -2.85, 75: -2.85, 80: -4.5, 85: -7.2, 90: -9.3 }
      },
      /**
       * Ceiling speaker LP configuration (C-IC6LP).
       * @type {Object}
       */
      ceilingspkr_lp: {
        modelName: 'Desono C-IC6LP',
        isCeilingSpkr: true,
        /**
         * Returns the icon identifier for a ceiling speaker.
         * @function
         * @returns {string} The icon identifier 'speaker'.
         */
        icon: () => 'speaker',
        /**
         * Calculates the height for a ceiling speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The calculated height, which is the minimum ceiling height.
         */
        height: (area) => area.ceilingHeight.min,
        /**
         * Determines the valid height range for a ceiling speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number[]} An array with min and max height values.
         */
        heightRange: (area) => [area.ceilingHeight.min, area.ceilingHeight.min],
        /**
         * Determines the coverage options for the speaker.
         * @function
         * @param {Object} area - The area object.
         * @returns {Object} Coverage options with 'focused' and 'full_room' properties.
         */
        coverage: (area) => ({ focused: true, full_room: false }),
        /**
         * Coverage angle for ceiling speaker calculations (in degrees).
         * @type {number}
         */
        coverageAngle: 39,
        /**
         * Sensitivity rating for the speaker (in dB).
         * @type {number}
         */
        sensitivity: 86.6,
        /**
         * Polar data for ceiling speaker at 100Hz.
         * Same as C-IC6 as they are the same driver.
         * @type {Object}
         */
        polarData: { 0: -0.9, 5: -0.4, 10: -0.9, 15: -1.5, 20: -1.35, 25: -1.3, 30: -1.3, 35: -1.95, 40: -1.85, 45: -2.1, 50: -2.75, 55: -2.25, 60: -3.25, 65: -3.6, 70: -2.85, 75: -2.85, 80: -4.5, 85: -7.2, 90: -9.3 }
      },
      /**
       * Ceiling speaker LP-TAA configuration (C-IC6LP-TAA).
       * @type {Object}
       */
      ceilingspkr_lp_taa: {
        modelName: 'Desono C-IC6LP-TAA',
        isCeilingSpkr: true,
        /**
         * Returns the icon identifier for a ceiling speaker.
         * @function
         * @returns {string} The icon identifier 'speaker'.
         */
        icon: () => 'speaker',
        /**
         * Calculates the height for a ceiling speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The calculated height, which is the minimum ceiling height.
         */
        height: (area) => area.ceilingHeight.min,
        /**
         * Determines the valid height range for a ceiling speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number[]} An array with min and max height values.
         */
        heightRange: (area) => [area.ceilingHeight.min, area.ceilingHeight.min],
        /**
         * Determines the coverage options for the speaker.
         * @function
         * @param {Object} area - The area object.
         * @returns {Object} Coverage options with 'focused' and 'full_room' properties.
         */
        coverage: (area) => ({ focused: true, full_room: false }),
        /**
         * Coverage angle for ceiling speaker calculations (in degrees).
         * @type {number}
         */
        coverageAngle: 39,
        /**
         * Sensitivity rating for the speaker (in dB).
         * @type {number}
         */
        sensitivity: 86.6,
        /**
         * Polar data for ceiling speaker at 100Hz.
         * Same as C-IC6 as they are the same driver.
         * @type {Object}
         */
        polarData: { 0: -0.9, 5: -0.4, 10: -0.9, 15: -1.5, 20: -1.35, 25: -1.3, 30: -1.3, 35: -1.95, 40: -1.85, 45: -2.1, 50: -2.75, 55: -2.25, 60: -3.25, 65: -3.6, 70: -2.85, 75: -2.85, 80: -4.5, 85: -7.2, 90: -9.3 }
      },
      /**
       * Pendant speaker configuration.
       * @type {Object}
       */
      pendantspkr: {
        modelName: 'Desono P6-SM',
        isPendantSpkr: true,
        mountingType: 'pendant',
        /**
         * Returns the icon identifier for a pendant speaker.
         * @function
         * @returns {string} The icon identifier 'speaker'.
         */
        icon: () => 'speaker',
        /**
         * Calculates the default height for a pendant speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The calculated default height.
         */
        height: (area) => Math.min(2.4384, area.ceilingHeight.min),
        /**
         * Determines the valid height range for a pendant speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number[]} An array with min and max height values.
         */
        heightRange: (area) => [1.8, area.ceilingHeight.min],
        /**
         * Determines the coverage settings for a pendant speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {Object} An object with focused and full_room boolean properties.
         */
        coverage: (area) => {
          const ck = values(area.speakers.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.speakers.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        /**
         * The coverage angle of the pendant speaker.
         * @type {number}
         */
        coverageAngle: 45,
        /**
         * The sensitivity of the pendant speaker.
         * @type {number}
         */
        sensitivity: 85.0,
        /**
         * The polar data @100Hz for the P6-SM speaker 
         */
        polarData: { 0: -0.8, 5: -0.3, 10: -0.8, 15: -1.4, 20: -1.25, 25: -1.2, 30: -1.2, 35: -1.85, 40: -1.75, 45: -2.0, 50: -2.65, 55: -2.15, 60: -3.15, 65: -3.5, 70: -2.75, 75: -2.75, 80: -4.4, 85: -7.1, 90: -9.2 }
      },
      /**
       * Wall speaker configuration.
       * @type {Object}
       */
      wallspkr: {
        modelName: 'Desono DX-S5',
        isWallSpkr: true,
        mountingType: 'wall',
        /**
         * Returns the icon identifier for a wall speaker.
         * @function
         * @returns {string} The icon identifier 'speaker'.
         */
        icon: () => 'speaker',
        /**
         * Calculates the default height for a wall speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {number} The wall speaker height from area.speakers.height or default.
         */
        height: () => 1.8288,  // 6 feet in meters
        /**
         * Determines the valid height range for a wall speaker.
         * @function
         * @returns {number[]} An array with min and max height values.
         */
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        /**
         * Determines the coverage settings for a wall speaker.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {Object} An object with focused and full_room boolean properties.
         */
        coverage: (area) => {
          const ck = values(area.speakers.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.speakers.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        /**
         * The coverage angle of the wall speaker.
         * @type {number}
         */
        coverageAngle: 90,
        /**
         * The sensitivity of the wall speaker.
         * @type {number}
         */
        sensitivity: 84.0,
        /**
         * The polar data @100Hz for the DX-S5 speaker 
         */
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      'wallspkr-dx-s8': {
        modelName: 'Desono DX-S8',
        isWallSpkr: true,
        mountingType: 'wall',
        icon: () => 'speaker',
        height: () => 1.8288,  // 6 feet in meters
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        coverage: (area) => {
          const ck = values(area.speakers.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.speakers.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 90,
        sensitivity: 86.0,
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      'wallspkr-ent206': {
        modelName: 'Desono ENT206',
        isWallSpkr: true,
        mountingType: 'wall',
        icon: () => 'speaker',
        height: () => 1.8288,  // 6 feet in meters
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        coverage: (area) => {
          const ck = values(area.speakers.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.speakers.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 90,
        sensitivity: 84.0,
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      'wallspkr-ex-s6': {
        modelName: 'Desono EX-S6',
        isWallSpkr: true,
        mountingType: 'wall',
        icon: () => 'speaker',
        height: () => 1.8288,  // 6 feet in meters
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        coverage: (area) => {
          const ck = values(area.speakers.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.speakers.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 90,
        sensitivity: 84.0,
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      'wallspkr-ex-s8': {
        modelName: 'Desono EX-S8',
        isWallSpkr: true,
        mountingType: 'wall',
        icon: () => 'speaker',
        height: () => 1.8288,  // 6 feet in meters
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        coverage: (area) => {
          const ck = values(area.speakers.coverage).some(x => x);
          const hasTable = Boolean(area.table.shape);
          return ck ? area.speakers.coverage : {
            focused: hasTable,
            full_room: !hasTable
          }
        },
        coverageAngle: 90,
        sensitivity: 86.0,
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      /**
       * Wall speaker DX-S8 configuration.
       * @type {Object}
       */
      wallspkr_dx8: {
        modelName: 'Desono DX-S8',
        isWallSpkr: true,
        icon: () => 'speaker',
        height: () => 1.8288,  // 6 feet in meters
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        coverage: () => ({ focused: true, full_room: false }),
        coverageAngle: 90,
        sensitivity: 85.0,
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      /**
       * Wall speaker EX-S6 configuration.
       * @type {Object}
       */
      wallspkr_ex6: {
        modelName: 'Desono EX-S6',
        isWallSpkr: true,
        icon: () => 'speaker',
        height: () => 1.8288,  // 6 feet in meters
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        coverage: () => ({ focused: true, full_room: false }),
        coverageAngle: 90,
        sensitivity: 84.5,
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      /**
       * Wall speaker MASK6C configuration.
       * @type {Object}
       */
      wallspkr_mask6c: {
        modelName: 'Desono MASK6C',
        isWallSpkr: true,
        mountingType: 'wall',
        icon: () => 'speaker',
        height: () => 1.8288,  // 6 feet in meters
        heightRange: (area) => [1.2192, area.ceilingHeight.min - 0.3048],  // Max is 1 foot below ceiling
        coverage: () => ({ focused: true, full_room: false }),
        coverageAngle: 90,
        sensitivity: 85.5,
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        targetSPL: [73, 73, 73, 71.5, 68]
      },
      /**
       * Bar speaker configuration.
       * @type {Object}
       */
      barspkr: {
        modelName: null,
        isBarSpkr: true,
        mountingType: 'ceiling',
        /**
         * Returns the icon identifier for a bar speaker based on the microphone model.
         * @function
         * @param {Object} area - The area object from the state.
         * @returns {string} The icon identifier based on the microphone model.
         */
        icon: (area) => {
          // Check if it's ABC (audio bar), CBC (ceiling/pendant bar), or VBC (video bar).
          // CBC's bar speaker shares the same `ceiling_bar` icon as its CBC mic (PIG-138) —
          // it must NOT use `audiobar`, which is the table/wall ABC icon (two side lenses,
          // no ceiling-mount stub).
          if (area.microphones?.modelName?.includes('CBC 2500')) {
            return 'ceiling_bar';
          } else if (area.microphones?.modelName === 'Parlé ABC 2500a') {
            return 'audiobar';
          } else if (area.microphones?.modelName === 'Parlé VBC 2500a') {
            return 'videobar';
          } else if (area.microphones?.modelName === 'Parlé VBC 2800') {
            return 'videobar2800';
          }
          // Default to videobar if model name is not specified
          return 'videobar';
        },
        /**
         * Returns the height for a bar speaker.
         * @function
         * @returns {number} The fixed height of the talker.
         */
        height: () => constants.area.talkerHeight,
        /**
         * Determines the coverage settings for a bar speaker.
         * @function
         * @returns {Object} An object with focused and full_room set to false.
         */
        coverage: () => ({
          focused: false,
          full_room: false
        }),
        /**
         * The coverage angle of the bar speaker.
         * @type {number}
         */
        coverageAngle: 90,
        /**
         * The sensitivity of the bar speaker.
         * @type {number}
         */
        sensitivity: 84,
        /**
         * The polar data @100Hz for the C-IC6 speaker 
         */
        polarData: { 0: 0, 5: -0.05, 10: -0.14, 15: -0.26, 20: -0.47, 25: -0.72, 30: -1.00, 35: -1.34, 40: -1.70, 45: -2.14, 50: -2.65, 55: -3.17, 60: -3.82, 65: -4.46, 70: -5.22, 75: -5.99, 80: -6.78, 85: -7.45, 90: -7.87 },
        /**
         * The target spl levels for different accooustic settings
         */
        targetSPL: [73, 73, 73, 71.5, 68]
      }
    },
    /**
     * NOT FULLY IMPLEMENTED YET
     * Camera configurations.
     * @type {Object.<string, Object>}
     */
    cameras: {
      /**
       * Vidi 250 camera configuration.
       * @type {Object}
       */
      vidi250: {
        modelName: 'Vidi 250',
        isBarCamera: false,
        /**
         * Returns the icon identifier for a standalone camera.
         * @function
         * @returns {string} The icon identifier 'camera' for standalone Vidi camera.
         */
        icon: () => 'camera',
        /**
         * The coverage angle of the VIDI 250 camera (full field of view).
         * @type {number}
         */
        coverageAngle: CAMERA_WIDE_IMAGER.coverageAngle,
        /**
         * The coverage radius of the VIDI 250 camera.
         * @type {number}
         */
        coverageRadius: CAMERA_WIDE_IMAGER.coverageRadius,
        /**
         * The limit radius of the VIDI 250 camera.
         * @type {number}
         */
        limitRadius: CAMERA_WIDE_IMAGER.limitRadius
      },

      /**
       * Vidi 280 camera configuration.
       * @type {Object}
       * Dual-imager camera with both wide-angle and zoom capabilities
       */
      vidi280: {
        modelName: 'Vidi 280',
        isBarCamera: false,
        dualImager: true,
        /**
         * Returns the icon identifier for a standalone camera.
         * @function
         * @returns {string} The icon identifier 'camera' for standalone Vidi camera.
         */
        icon: () => 'camera',
        /**
         * Wide-angle imager configuration (uses shared dual-imager specs)
         * @type {Object}
         */
        wideImager: {
          /**
           * The coverage angle of the wide-angle imager (full field of view).
           * @type {number}
          */
          coverageAngle: 122,
          /**
           * The coverage radius of the wide-angle imager.
           * @type {number}
          */
          coverageRadius: 5,
          /**
           * The limit radius of the wide-angle imager.
           * @type {number}
          */
          limitRadius: 9,
        },
        /**
         * Zoom imager configuration (uses shared dual-imager specs).
         * @type {Object}
         */
        zoomImager: CAMERA_ZOOM_IMAGER
      },

      /**
       * No camera selected.
       * @type {Object}
       */
      none: {
        modelName: 'None',
        isBarCamera: false,
        /**
         * Returns the icon identifier for no camera.
         * @function
         * @returns {string} The icon identifier 'camera'.
         */
        icon: () => 'camera',
        /**
         * The coverage angle - not applicable for none.
         * @type {number}
         */
        coverageAngle: 0,
        /**
         * The coverage radius - not applicable for none.
         * @type {number}
         */
        coverageRadius: 0,
        /**
         * The limit radius - not applicable for none.
         * @type {number}
         */
        limitRadius: 0
      },

      /**
       * standAlone camera configuration.
       * @description: This is for the vidi_250 but eventually the vidi_280 as well
       * @type {Object}
       */
      standAlone: {
        modelName: 'Vidi 250',
        isBarCamera: false,
        /**
         * Returns the icon identifier for a ceiling speaker.
         * @function
         * @returns {string} The icon identifier 'speaker'.
         */
        icon: () => 'camera',
        /**
         * The coverage angle of the VIDI 250 camera.
         * @type {number}
         */
        coverageAngle: CAMERA_WIDE_IMAGER.coverageAngle,
        /**
         * The coverage radius of the VIDI 250 camera.
         * @type {number}
         */
        coverageRadius: CAMERA_WIDE_IMAGER.coverageRadius,
        /**
         * The limit radius of the VIDI 250 camera.
         * @type {number}
         */
        limitRadius: CAMERA_WIDE_IMAGER.limitRadius
      },

      /**
       * bar camera configuration.
       * @description: This is for the VBC 2500 which is the same specs as the Vidi 250. Eventually it will include the VBC 2800 which uses the Vidi 280 camera 
       * @type {Object}
       */
      barCamera: {
        modelName: 'Parlé VBC 2500a',
        isBarCamera: true,
        /**
         * Returns the icon identifier for the bar camera.
         * @function
         * @returns {string} The icon identifier - 'videobar' for VBC 2500a, 'camera' for ABC 2500a with external camera.
         */
        icon: () => 'videobar',
        /**
         * The coverage angle of the VIDI 250 camera.
         * @type {number}
         */
        coverageAngle: 109,
        /**
         * The coverage radius of the VIDI 250 camera.
         * @type {number}
         */
        coverageRadius: 5,
        /**
         * The limit radius of the VIDI 250 camera.
         * @type {number}
         */
        limitRadius: 9
      },

      /**
       * VBC 2800 bar camera configuration with dual-imager (uses Vidi 280 camera specs).
       * @type {Object}
       */
      barCamera2800: {
        modelName: 'Parlé VBC 2800',
        isBarCamera: true,
        dualImager: true,
        /**
         * Returns the icon identifier for the VBC 2800 bar camera.
         * @function
         * @returns {string} The icon identifier 'videobar2800' for VBC 2800.
         */
        icon: () => 'videobar2800',
        /**
         * Wide-angle imager configuration (uses shared dual-imager specs)
         * @type {Object}
         */
        wideImager: CAMERA_WIDE_IMAGER,
        /**
         * Zoom imager configuration (uses shared dual-imager specs)
         * @type {Object}
         */
        zoomImager: CAMERA_ZOOM_IMAGER
      }

    }
  },

  // Define the plot elements to be included in the report
  reportPlotElements: ['ceiling', 'partitions', 'door', 'devices', 'furniture'],

  /**
    * @constant equipmentLists
    * @description Configuration object for different equipment list displays, using a standardized positioning config creator.
    * 
    * @property {Object} summary - Basic equipment summary configuration
    * @property {Function} summary.title - Generates list title based on area name and content
    * @property {Array<number>} summary.widths - Column width percentages for summary view
    * 
    * @property {Object} microphonePositioning - Microphone positioning configuration
    * @property {Object} speakerPositioning - Speaker positioning configuration
    * @property {Object} cameraPositioning - Camera positioning configuration
    * 
    * @example
    * // Summary usage
    * const summaryTitle = equipmentLists.summary.title("Room 101", false);
    * // Returns "Room 101 equipment list"
    * 
    * @see createPositioningConfig
    */

  /**
  * @function createPositioningConfig
  * @description Creates standardized configuration for device positioning details
  * 
  * @param {string} deviceType - Type of device ('microphones', 'speakers', 'cameras')
  * @param {string} deviceName - Singular name of device for title
  * @param {Array<string>} [additionalNotes] - Device-specific measurement notes
  * 
  * @returns {Object} Positioning configuration object
  * @property {Function} title - Title generator function
  * @property {Array<number>} widths - Column width percentages
  * @property {Array<string>} notes - Combined standard and device-specific notes
  * 
  * @example
  * const micConfig = createPositioningConfig(
  *   'microphones',
  *   'microphone',
  *   ['Height is measured from floor']
  * );
  * 
  * @description
  * Creates standardized positioning configurations with:
  * - Device-specific titles and widths
  * - Standard measurement references
  * - Optional additional notes per device type
  * 
  * Specific Implementations:
  * 1. Microphones:
  *    - Includes floor height reference
  * 
  * 2. Speakers:
  *    - Uses standard positioning notes
  * 
  * 3. Cameras:
  *    - Custom angle reference (0° pointing right)
  */
  equipmentLists: {
    summary: {
      title: (name, isEmpty) => `${name} ${isEmpty ? 'does not have equipment' : 'equipment list'}`,
      widths: [10, 25, 65],
      list: 'areaEquipmentList',
    },
    microphonePositioning: {
      ...createPositioningConfig('microphones', 'microphone', ['Height is measured from floor'])
    },
    speakerPositioning: {
      ...createPositioningConfig('speakers', 'loudspeaker')
    },
    cameraPositioning: {
      ...createPositioningConfig('cameras', 'camera', ['Horizontal angle is clockwise with 0 deg reference pointing right'])
    }
  },

  // This are the keys I want to use to save 
  saveKeys: {
    uiKeys: ['units', 'scale', 'savekey'],
    containerKeys: ['project', 'requestedQuote'],
    areaKeys: ['id', 'name', 'dimensions', 'location', 'ceilingHeight', 'areaOrientation', 'door', 'ceilingTiles', 'table', 'acoustics', 'microphones', 'speakers', 'cameras', 'hasBar', 'customized', 'productFamily', 'ucCompute', 'platform', 'equipmentList',
      // Expansion mic + speaker state — user intent that can't be recomputed:
      // the type choice, the coverage toggle, the manual-add flag, and the
      // placed device arrays. The *Recommended flags are intentionally omitted;
      // the gap detector re-fires on the post-load recalc and re-derives them.
      'expansionMicType', 'expansionMicCoverage', 'expansionMicrophones', 'expansionMicManualOptIn',
      'expansionSpeakerType', 'expansionSpeakerCoverage', 'expansionSpeakers',
      // Devio UCX capacity fallback (spec §2.1/§2.6) — user intent + a
      // degrades-only guarantee, neither of which can be recomputed:
      // `androidExplicitlySelected` records a direct platform-selector pick,
      // and `_ucxCapacityFallenBack` must keep blocking a promote-back-to-
      // Devio/bar after reload, not just within the session it fired in.
      // `ucxMicTypeSwitchAllowed` is deliberately omitted — like the
      // *Recommended flags above, it's re-derived by the post-load recalc.
      'androidExplicitlySelected', '_ucxCapacityFallenBack',
    ],
    areaOmitKeys: []  // No longer excluding plot data - needed for accurate PDF heatmap rendering
    // todo: add equipment list, request quote
  },

  // Extra save paramerters to be added on Save
  extraSaveParameters: (area) => ({
  }),

  // Extra load paramters to be added onLoad
  extraLoadParameters: (area) => {
    // Migrate camera plotVisibility for dual-imager cameras (Vidi 280)
    if (area.cameras && (area.cameras.model === 'vidi280' || area.cameras.modelName === 'Vidi 280')) {
      // If old format with 'both' option, convert to 'wide_only'
      if (area.cameras.plotVisibility?.both) {
        area.cameras.plotVisibility = {
          wide_only: true,
          zoom_only: false
        };
      }
      // If plotVisibility doesn't exist at all, initialize it
      else if (!area.cameras.plotVisibility) {
        area.cameras.plotVisibility = {
          wide_only: true,
          zoom_only: false
        };
      }
    }
    return {};
  },




  // placeholder - not implemented yet - used for recalculation
  containerParams: [],
  // not a placeholder
  areaParams: ['microphones', 'speakers', 'cameras', 'productFamily', 'ceilingTiles', 'acoustics', 'table'],

  // Export the cached calculation function for use in modals
  getCachedCeilingMicCalculation,

  // Export the CBC height calculation function and height range for use in validation
  calculateCBCHeight,
  cbcHeightRange,

};

export default constants
